<script setup>
import flatpickr from 'flatpickr';
import { useValidation, useFormContext } from '/app/composables/form';
import { nanoid } from 'nanoid';
import { cn } from '/app/utils/helpers';
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick, useTemplateRef } from 'vue';
import NIcon from '/app/components/NIcon.vue';

defineOptions({
    inheritAttrs: false,
});

const props = defineProps({
    name: { type: String, default: null },
    label: { type: String, default: null },
    hint: { type: String, default: null },
    fieldName: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    forceError: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
    modelValue: {
        type: [String, Date],
        default: '',
    },
    dateFormat: {
        type: String,
        default: 'Y-m-d',
    },
    range: {
        type: Boolean,
        default: false,
    },
    rounded: {
        type: Boolean,
        default: false,
    },
    enableTime: {
        type: Boolean,
        default: false,
    },
    format: {
        type: String,
        default: 'F j, Y', // Changed default to a more human-readable format
    },
    endTime: {
        type: Object,
    },
    minDate: {
        type: [Date, Object, String],
        default: '1900-01',
    },
    maxDate: {
        type: [Date, Object, String],
        default: '2099-12',
    },
    inputClass: {
        type: [String, Array, Object],
    },
    placeholder: {
        type: String,
    },
});

const emit = defineEmits(['update:modelValue', 'change', 'close', 'blur']);

const form = useFormContext();
const { invalid, error, attrs, clearErrors, setError, focus, target } = useValidation(props);

const dateInput = useTemplateRef('dateInput');
const fp = ref(null);

const id = computed(() => props.name || nanoid());

function update(value) {
    form.errors.clear(id.value);
    emit('update:modelValue', value);
    emit('change', value);
}

function clear() {
    fp.value?.clear();
    update(null);
}

watch(() => props.modelValue, (newValue) => {
    if (fp.value && newValue) {
        try {
            fp.value.setDate(newValue, false);
        } catch (e) {
            console.warn('Invalid date format:', newValue);
        }
    }
});

watch(() => props.minDate, (newValue) => {
    if (fp.value) {
        fp.value.set('minDate', newValue);
    }
});

watch(() => props.maxDate, (newValue) => {
    if (fp.value) {
        fp.value.set('maxDate', newValue);
    }
});

onMounted(() => {
    nextTick(() => {
        fp.value = flatpickr(dateInput.value, {
            static: true,
            enableTime: props.enableTime,
            enableSeconds: false,
            altInput: true,
            dateFormat: props.dateFormat, // Format saved to modelValue
            altFormat: props.format, // Format displayed to user
            allowInput: true,
            minDate: props.minDate,
            maxDate: props.maxDate,
            mode: props.range ? 'range' : 'single',
            onChange: (selDates) => {
                if (selDates.length > 0) {
                    const formattedDate = fp.value.formatDate(selDates[0], props.dateFormat);
                    update(formattedDate);
                }

                if (props.endTime) {
                    // The parent passes another <Datepicker> via template ref. We
                    // exposed `flatpickr` (the instance) via defineExpose, so we
                    // can reach into it the same way the Options API version did.
                    props.endTime.flatpickr?.set('minDate', selDates[0]);
                }
            },
            onClose: () => {
                if (fp.value.selectedDates.length === 0) {
                    update(null);
                }

                form.errors.clear(id.value);
            },
        });

        // Set initial date if provided
        if (props.modelValue) {
            try {
                fp.value.setDate(props.modelValue, false);
            } catch (e) {
                console.warn('Invalid initial date format:', props.modelValue);
            }
        }
    });
});

onBeforeUnmount(() => {
    if (fp.value) {
        fp.value.destroy();
    }
});

// Expose `flatpickr` (matching the Options API field name) so parent components
// passing this datepicker via :end-time can still call `.flatpickr.set(...)`.
// Also expose `clear` for symmetry with the previous public surface.
defineExpose({
    get flatpickr() {
        return fp.value;
    },
    clear,
});
</script>

<template>
    <div class="form-group__helper is-datepicker" :class="{ 'is-range': range }">
        <label v-if="label" :for="id" class="form-group__label whitespace-nowrap" style="display: block;">{{ label }}</label>
        <span v-if="hint" class="form-group__hint" style="display: block;">{{ hint }}</span>
        <label :for="id" :class="cn('form-group__input-wrapper m-0 flex items-center border border-dust-300 bg-white rounded-md', inputClass)">
            <input
                v-bind="attrs"
                class="form-group__input !bg-transparent pl-6"
                type="text"
                ref="dateInput"
                :class="{ 'is-error': invalid, 'is-rounded': rounded }"
                :id="id"
                :name="id"
                :disabled="disabled"
                :placeholder="placeholder"
            >
            <NIcon as="calendar-date-picker" class="ml-1 mt-[2px]" />
        </label>
        <span v-if="invalid" class="form-group__error">{{ error }}</span>
    </div>
</template>

<style lang="scss">
    .flatpickr-calendar.static.open {
        position: absolute !important;
    }
</style>
